import { CopyProgramSchedulePaymentStudentType } from "../../../types/dto/scheduleStudent";

export {
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_LABELS,
  GLOBAL_PAYMENT_STATUS_COLORS,
} from "../../../shared/constants/academyStatusColors";

export type { PaymentGlobalStatus } from "../../../shared/constants/academyStatusColors";

function isPaymentOverdue(
  paymentDueTimestamp: number,
  balance: number | null | undefined
) {
  const currentTimestamp = Date.now() / 1000;
  return (
    paymentDueTimestamp < currentTimestamp &&
    ((balance ?? 0) > 0 || balance === null)
  );
}

export function calculateScheduleQuote(
  schedule: CopyProgramSchedulePaymentStudentType
) {
  const paymentDueTimestamp = schedule.payment_due_date
    ? new Date(schedule.payment_due_date as string | Date).getTime() / 1000
    : null;
  const balance = schedule.balance;
  const isOverdue =
    paymentDueTimestamp != null &&
    isPaymentOverdue(paymentDueTimestamp, balance);

  const subtotal = Number(schedule.program_payment_schedule_price || 0);
  const promptPrice = Number(
    schedule.program_payment_schedule_prompt_price || 0
  );
  let cashDiscountAmount = 0;
  let total = 0;

  if (!isOverdue && promptPrice > 0) {
    if ((balance ?? 0) > 0 || balance === null) {
      cashDiscountAmount = 0;
      total = Number(balance ?? 0);
    } else {
      cashDiscountAmount = subtotal - promptPrice;
      total = promptPrice - Number(schedule.amount_paid ?? 0);
    }
  } else if ((balance ?? 0) > 0 || balance === null) {
    total = subtotal - Number(schedule.amount_paid ?? 0);
  } else {
    total = subtotal;
  }

  const isPromptPaymentEligible =
    !isOverdue && promptPrice > 0 && schedule.payment_status !== "paid";

  return {
    subtotal,
    promptPrice,
    cashDiscountAmount,
    total: Math.max(0, total),
    isOverdue,
    isPromptPaymentEligible,
  };
}

export function calculateQuoteForSchedules(
  schedules: CopyProgramSchedulePaymentStudentType[]
) {
  return schedules.reduce(
    (quote, schedule) => {
      const { subtotal, cashDiscountAmount, total } =
        calculateScheduleQuote(schedule);
      return {
        subtotal: quote.subtotal + subtotal,
        cash_discount_amount: quote.cash_discount_amount + cashDiscountAmount,
        total: quote.total + total,
        totalPrompt:
          quote.totalPrompt +
          Number(
            schedule.program_payment_schedule_prompt_price ||
              schedule.program_payment_schedule_price ||
              0
          ),
      };
    },
    { subtotal: 0, cash_discount_amount: 0, total: 0, totalPrompt: 0 }
  );
}

export function enrichSchedule(
  schedule: CopyProgramSchedulePaymentStudentType
): {
  effectiveBalance: number;
  cashDiscountAmount: number;
  isPromptPaymentEligible: boolean;
  promptPrice: number;
} & CopyProgramSchedulePaymentStudentType {
  const quote = calculateScheduleQuote(schedule);
  const price = Number(schedule.program_payment_schedule_price || 0);
  const effectiveBalance = Math.max(0, Number(schedule.balance ?? quote.total));

  let cashDiscountAmount = Number(schedule.cash_discount_amount ?? 0);
  if (
    !cashDiscountAmount &&
    quote.isPromptPaymentEligible &&
    price > quote.promptPrice
  ) {
    cashDiscountAmount = price - quote.promptPrice;
  }

  return {
    ...schedule,
    effectiveBalance,
    cashDiscountAmount,
    isPromptPaymentEligible: quote.isPromptPaymentEligible,
    promptPrice: quote.promptPrice,
  };
}

export type MonthPaymentValidity = "vigente" | "no_vigente" | "sin_cuota";

export function getCurrentMonthSchedule(
  schedules: CopyProgramSchedulePaymentStudentType[]
) {
  const now = new Date();
  const active = schedules.filter((schedule) => schedule.status === "active");

  const matchesMonth = (value: CopyProgramSchedulePaymentStudentType["month"]) => {
    if (!value) return false;
    const date = new Date(value as string | Date);
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth()
    );
  };

  const matchesDueDate = (
    value: CopyProgramSchedulePaymentStudentType["payment_due_date"]
  ) => {
    if (!value) return false;
    const date = new Date(value as string | Date);
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth()
    );
  };

  return (
    active.find((schedule) => matchesMonth(schedule.month)) ??
    active.find((schedule) => matchesDueDate(schedule.payment_due_date)) ??
    null
  );
}

export function computeCurrentMonthPaymentValidity(
  schedule: CopyProgramSchedulePaymentStudentType | null
): { validity: MonthPaymentValidity; label: string } {
  if (!schedule) {
    return { validity: "sin_cuota", label: "Sin cuota asignada" };
  }

  const quote = calculateScheduleQuote(schedule);
  const balance = Math.max(0, Number(schedule.balance ?? quote.total ?? 0));

  if (schedule.payment_status === "paid" || balance <= 0) {
    return { validity: "vigente", label: "Vigente" };
  }

  return { validity: "no_vigente", label: "No vigente" };
}
